import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { Plus, X } from 'lucide-react'

import type { IrrigationScheduleFormValues } from '@/lib/schemas/irrigation-schemas'
import { Button } from '@/components/ui/button'
import { Field, FieldError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { WEEKDAYS, weekdayLabel } from '@/lib/irrigation-schedule'
import { m } from '@/lib/paraglide/messages'

/** Chọn các thứ trong tuần: mỗi thứ là một nút bật/tắt. */
export function WeekdayPicker({ disabled }: { disabled: boolean }) {
    const { control } = useFormContext<IrrigationScheduleFormValues>()
    return (
        <div className="space-y-1.5">
            <Label required>{m.irrigation_schedule_weekdays()}</Label>
            <Controller
                control={control}
                name="weekdays"
                render={({ field }) => (
                    <div className="flex flex-wrap gap-1.5">
                        {WEEKDAYS.map((day) => {
                            const on = field.value.includes(day)
                            return (
                                <Button
                                    key={day}
                                    type="button"
                                    size="sm"
                                    variant={on ? 'default' : 'outline'}
                                    aria-pressed={on}
                                    disabled={disabled}
                                    className="min-w-11"
                                    onClick={() =>
                                        field.onChange(
                                            on
                                                ? field.value.filter(
                                                      (d) => d !== day,
                                                  )
                                                : [...field.value, day],
                                        )
                                    }
                                >
                                    {weekdayLabel(day)}
                                </Button>
                            )
                        })}
                    </div>
                )}
            />
            <FieldError name="weekdays" />
        </div>
    )
}

/** Giờ cố định: danh sách giờ bắt đầu, thêm/xoá được. */
export function FixedTimesField({ disabled }: { disabled: boolean }) {
    const { control, register } = useFormContext<IrrigationScheduleFormValues>()
    const times = useFieldArray({ control, name: 'start_times' })
    return (
        <div className="space-y-1.5">
            <Label required>{m.irrigation_schedule_start_times()}</Label>
            <div className="flex flex-wrap gap-2">
                {times.fields.map((item, i) => (
                    <div key={item.id} className="space-y-1">
                        <div className="flex items-center gap-1">
                            <Input
                                type="time"
                                aria-label={`${m.irrigation_schedule_start_times()} ${i + 1}`}
                                className="w-32"
                                disabled={disabled}
                                {...register(`start_times.${i}.value`)}
                            />
                            {times.fields.length > 1 && !disabled && (
                                <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    aria-label={m.irrigation_schedule_remove_time()}
                                    onClick={() => times.remove(i)}
                                >
                                    <X className="size-4" />
                                </Button>
                            )}
                        </div>
                        <FieldError name={`start_times.${i}.value`} />
                    </div>
                ))}
            </div>
            {!disabled && (
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => times.append({ value: '' })}
                >
                    <Plus className="size-4" />
                    {m.irrigation_schedule_add_time()}
                </Button>
            )}
            <FieldError name="start_times" />
        </div>
    )
}

/** Chu kỳ: từ giờ nào đến giờ nào, lặp lại mỗi bao nhiêu phút. */
export function IntervalFields({ disabled }: { disabled: boolean }) {
    const { register } = useFormContext<IrrigationScheduleFormValues>()
    return (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Field
                name="window_start"
                label={m.irrigation_schedule_window_start()}
                required
            >
                <Input
                    id="window_start"
                    type="time"
                    disabled={disabled}
                    {...register('window_start')}
                />
            </Field>
            <Field
                name="window_end"
                label={m.irrigation_schedule_window_end()}
                required
            >
                <Input
                    id="window_end"
                    type="time"
                    disabled={disabled}
                    {...register('window_end')}
                />
            </Field>
            <Field
                name="every_minutes"
                label={m.irrigation_schedule_every()}
                required
            >
                <Input
                    id="every_minutes"
                    inputMode="numeric"
                    disabled={disabled}
                    {...register('every_minutes')}
                />
            </Field>
        </div>
    )
}
